import { cssThemedValue } from '@creditornot/cb-ingredients';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { BusinessRepresentative } from 'apis/legitimizer/types';

import { InfoSubRow } from './InfoSubRow';
import MissingFieldError from './MissingFieldError';
import { countryByCode } from '../countryCodes';
import { SectionConfiguration } from '../types';
import { useBusinessRepresentativeFieldsConfigMemo } from '../useFieldsConfigMemo';

type Props = {
  businessRepresentative: BusinessRepresentative;
  sectionConfiguration: SectionConfiguration;
  setHasMissingFields?: (value: boolean) => void;
};

const Row = styled.span`
  display: block;

  & + * {
    margin-block-start: 16px;
  }
`;

const Divider = styled.div`
  margin-block-start: 8px;
  border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
`;

export const BusinessRepresentativeInfo = ({
  businessRepresentative,
  sectionConfiguration,
  setHasMissingFields,
}: Props) => {
  const intl = useIntl();
  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

  const businessRepresentativeFieldsConfig = useBusinessRepresentativeFieldsConfigMemo({
    sectionConfiguration,
    entityUUID: true,
  });

  return (
    <Row key={businessRepresentative.entityUUID}>
      {businessRepresentativeFieldsConfig.name.visible && (
        <InfoSubRow title={t('compliance.kyb.sections.basic-info.company-representatives.full-name')}>
          {businessRepresentative.fullName?.value || (
            <MissingFieldError
              shouldShow={businessRepresentativeFieldsConfig.name.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </InfoSubRow>
      )}
      {businessRepresentativeFieldsConfig.dateOfBirth.visible && (
        <InfoSubRow title={t('compliance.kyb.person-info.date-of-birth')}>
          {businessRepresentative.dateOfBirth?.value || (
            <MissingFieldError shouldShow={businessRepresentativeFieldsConfig.dateOfBirth.required} />
          )}
        </InfoSubRow>
      )}
      {businessRepresentativeFieldsConfig.placeOfBirth.visible && (
        <InfoSubRow title={t('compliance.kyb.person-info.place-of-birth')}>
          {businessRepresentative.placeOfBirth?.value || (
            <MissingFieldError shouldShow={businessRepresentativeFieldsConfig.placeOfBirth.required} />
          )}
        </InfoSubRow>
      )}
      {businessRepresentativeFieldsConfig.countryOfResidence.visible && (
        <InfoSubRow title={t('compliance.kyb.sections.basic-info.business-information.country-of-residence')}>
          {countryByCode(businessRepresentative.countryOfResidence?.value) || (
            <MissingFieldError
              shouldShow={businessRepresentativeFieldsConfig.countryOfResidence.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </InfoSubRow>
      )}
      {businessRepresentativeFieldsConfig.nationality.visible && (
        <InfoSubRow title={t('compliance.kyb.person-info.nationality')}>
          {countryByCode(businessRepresentative.nationality?.value) || (
            <MissingFieldError
              shouldShow={businessRepresentativeFieldsConfig.nationality.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </InfoSubRow>
      )}
      {businessRepresentativeFieldsConfig.nationalId.visible && (
        <InfoSubRow title={t('compliance.kyb.sections.basic-info.business-information.national-id')}>
          {businessRepresentative.nationalId?.value || (
            <MissingFieldError
              shouldShow={businessRepresentativeFieldsConfig.nationalId.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </InfoSubRow>
      )}
      <Divider />
    </Row>
  );
};
